import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Check, Loader2, ShieldCheck, Smartphone, MailCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { STEP_ICONS, AVAILABILITY_META, API_BASE } from './constants';
import { isValidPhone, cleanPhoneInput } from './utils';
import { assetUrl } from '@/lib/apiBase';
import { citiesToOffer, cityKeptOrFilled } from '@/lib/plzOrt';

export function FullscreenShell({ children, tenantName, logoUrl, title = 'Online-Anmeldung', slug }) {
  const fullLogo = logoUrl ? assetUrl(logoUrl) : null;
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-5 md:py-6 flex items-center gap-4">
          {fullLogo && (
            <img src={fullLogo} alt={tenantName || ''} className="h-14 w-auto" data-testid="public-tenant-logo" />
          )}
          <div className="min-w-0">
            <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-slate-900 leading-tight" data-testid="public-registration-heading">
              {title}
            </h1>
            <p className="text-base md:text-lg text-slate-600 mt-0.5" data-testid="public-registration-tenant-name">
              {tenantName || 'Musikschule'}
            </p>
          </div>
          {slug && (
            <div className="ml-auto flex flex-col sm:flex-row gap-2 shrink-0">
              <a href={`/anmeldung/${encodeURIComponent(slug)}/kuendigung`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50" data-testid="public-cancellation-link">
                Vertrag kündigen
              </a>
              <a href={`/anmeldung/${encodeURIComponent(slug)}/widerruf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-md bg-brand-red px-3 py-2 text-sm font-medium text-white hover:bg-brand-red/90" data-testid="public-withdrawal-link">
                Vertrag widerrufen
              </a>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 flex items-start justify-center p-4 md:p-8">
        {children}
      </main>
      <footer className="py-4 text-center text-xs text-slate-400">
        Powered by Musikschulverwaltung
      </footer>
    </div>
  );
}

export function Stepper({ steps, stepIdx, furthest = stepIdx, open = true, onToggle, renderPanel }) {
  return (
    <ol className="flex flex-col gap-3" data-testid="public-registration-stepper">
      {steps.map((s, i) => {
        const done = i < furthest;
        const active = i === stepIdx;
        const locked = i > furthest;
        const expanded = active && open;
        const Icon = STEP_ICONS[s.key];
        return (
          <li
            key={s.key}
            className={`bg-white border rounded-2xl shadow-sm overflow-hidden ${
              expanded ? 'border-brand-red/40' : 'border-slate-200'
            }`}
          >
            <button
              type="button"
              disabled={locked}
              aria-expanded={expanded}
              onClick={() => { if (!locked) onToggle?.(i); }}
              className="w-full flex items-center gap-3 px-4 py-4 text-left disabled:cursor-default"
              data-testid={`public-registration-step-toggle-${i}`}
            >
              <span
                className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center border-2 ${
                  done
                    ? 'bg-amber-400 border-amber-400 text-amber-950'
                    : active
                      ? 'bg-brand-red border-brand-red text-white ring-4 ring-brand-red/20'
                      : 'bg-white border-slate-200 text-slate-400'
                }`}
                data-testid={done ? `public-registration-step-check-${i}` : `public-registration-step-${i}`}
                aria-current={active ? 'step' : undefined}
              >
                {done ? <Check className="h-5 w-5" strokeWidth={3} /> : Icon ? <Icon className="h-4 w-4" /> : (
                  <span className="text-sm font-semibold">{i + 1}</span>
                )}
              </span>
              <span className="min-w-0">
                <span
                  className={`block text-base leading-snug ${
                    active ? 'text-brand-red font-semibold' : 'text-slate-800'
                  }`}
                  data-testid={`public-registration-step-label-${i}`}
                >
                  {s.label}
                </span>
                <span className={`block text-sm mt-0.5 ${active ? 'text-brand-red/80' : 'text-slate-500'}`}>
                  Schritt {i + 1} von {steps.length}
                </span>
              </span>
            </button>
            {expanded && renderPanel && (
              <div className="px-4 pb-6 md:px-8 border-t border-slate-100 pt-5" data-testid={`public-registration-step-panel-${i}`}>
                {renderPanel(s, i)}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function FieldLabel({ spec, error }) {
  return <Label className={error ? 'text-red-600' : ''}>{spec.label}{spec.required ? ' *' : ''}</Label>;
}

export function usePlzCities(plz) {
  const [cities, setCities] = useState([]);
  useEffect(() => {
    const p = (plz || '').trim();
    if (!/^\d{5}$/.test(p)) { setCities([]); return undefined; }
    let cancelled = false;
    axios.get(`${API_BASE}/public/registrations/geo/plz/${p}`)
      .then((r) => { if (!cancelled) setCities(r.data.cities || []); })
      .catch(() => { if (!cancelled) setCities([]); });
    return () => { cancelled = true; };
  }, [plz]);
  return cities;
}

export function CitySuggestions({ cities, current, onPick, testPrefix }) {
  const offered = citiesToOffer(current, cities);
  if (!offered.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid={`${testPrefix}-city-suggestions`}>
      <span className="text-xs text-slate-500">Orte zur PLZ:</span>
      {offered.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onPick(c)}
          className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
            c === current
              ? 'bg-brand-red border-brand-red text-white'
              : 'bg-white border-slate-300 text-slate-700 hover:border-brand-red/40 hover:text-brand-red'
          }`}
          data-testid={`${testPrefix}-city-suggestion-${c}`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

export function PhoneHint({ value, testId }) {
  const v = (value || '').trim();
  if (!v) return null;
  return isValidPhone(v) ? (
    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1" data-testid={testId}>
      <Check className="h-3 w-3" /> Gültige Rufnummer
    </p>
  ) : (
    <p className="text-xs text-red-600 mt-1" data-testid={testId}>
      Ungültige Rufnummer — z.&nbsp;B. +49 251 123456
    </p>
  );
}

export function PhoneField({ value, onChange, error, testId, placeholder = '+49 441 123456' }) {
  const [showHint, setShowHint] = useState(!!(value || '').trim());
  return (
    <>
      <Input
        placeholder={placeholder}
        inputMode="tel"
        className={error ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : ''}
        value={value}
        onChange={(e) => onChange(cleanPhoneInput(e.target.value))}
        onFocus={() => setShowHint(false)}
        onBlur={() => setShowHint(true)}
        data-testid={testId}
      />
      {showHint && <PhoneHint value={value} testId={`${testId}-hint`} />}
    </>
  );
}

const VERIFY_LABELS = {
  phone: {
    button: 'Rufnummer per SMS bestätigen',
    sent: 'Wir haben einen 6-stelligen Code an Ihre Rufnummer gesendet.',
    done: 'Rufnummer per SMS bestätigt',
    requiredHint: 'Bitte bestätigen Sie Ihre Rufnummer per SMS-Code.',
  },
  email: {
    button: 'E-Mail-Adresse bestätigen',
    sent: 'Wir haben einen 6-stelligen Code an Ihre E-Mail-Adresse gesendet.',
    done: 'E-Mail-Adresse bestätigt',
    requiredHint: 'Bitte bestätigen Sie Ihre E-Mail-Adresse per Code.',
  },
};

export function VerifyCodeBlock({ tenantSlug, kind, value, valueOk, verification, setVerification, requiredError }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const L = VERIFY_LABELS[kind];
  const matches = verification && verification.value_raw === value;
  const verified = matches && verification.verified;
  const codeSent = matches && !verification.verified;

  if (!valueOk) return null;

  const apiError = (e, fallback) => {
    const d = e?.response?.data?.detail;
    setError(typeof d === 'string' ? d : fallback);
  };

  const sendCode = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(
        `${API_BASE}/public/registrations/${encodeURIComponent(tenantSlug)}/${kind}/send-code`,
        { [kind]: value },
      );
      setVerification({ id: r.data.verification_id, value_raw: value, verified: false });
      setCode('');
    } catch (e) { apiError(e, 'Code-Versand fehlgeschlagen'); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(
        `${API_BASE}/public/registrations/${encodeURIComponent(tenantSlug)}/${kind}/verify-code`,
        { verification_id: verification.id, code: code.trim() },
      );
      setVerification({ ...verification, verified: true });
    } catch (e) { apiError(e, 'Bestätigung fehlgeschlagen'); } finally { setBusy(false); }
  };

  if (verified) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid={`public-registration-${kind}-verified`}>
        <ShieldCheck className="h-4 w-4 shrink-0" /> {L.done}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2" data-testid={`public-registration-${kind}-verify-block`}>
      {!codeSent ? (
        <Button type="button" variant="outline" size="sm" onClick={sendCode} disabled={busy} data-testid={`public-registration-${kind}-sendcode-btn`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : (kind === 'phone' ? <Smartphone className="h-3.5 w-3.5 mr-1.5" /> : <MailCheck className="h-3.5 w-3.5 mr-1.5" />)}
          {L.button}
        </Button>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          <p className="text-xs text-slate-600">{L.sent}</p>
          <div className="flex items-center gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              maxLength={6}
              className="w-32 font-mono tracking-widest"
              data-testid={`public-registration-${kind}-code-input`}
            />
            <Button type="button" size="sm" onClick={verify} disabled={busy || code.length !== 6} data-testid={`public-registration-${kind}-verify-btn`}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
              Bestätigen
            </Button>
            <button type="button" className="text-xs text-brand-red underline disabled:opacity-50" onClick={sendCode} disabled={busy} data-testid={`public-registration-${kind}-resend-btn`}>
              Code erneut senden
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-xs text-red-600" data-testid={`public-registration-${kind}-verify-error`}>{error}</p>}
      {requiredError && (
        <p className="text-xs text-red-600 font-medium" data-testid={`public-registration-${kind}-verify-required`}>{L.requiredHint}</p>
      )}
    </div>
  );
}

export function CustomFields({ customFields, form, setCustom, testPrefix, invalid = () => false }) {
  if (!customFields.length) return null;
  const err = (cf) => invalid(`custom.${cf.id}`);
  const ec = (cf) => (err(cf) ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : '');
  return (
    <div className="space-y-4">
      {customFields.map((cf) => {
        const val = form.custom[cf.id];
        const tid = `${testPrefix}-custom-${cf.id}`;
        if (cf.type === 'checkbox') {
          return (
            <label key={cf.id} className="flex items-start gap-3 text-sm cursor-pointer" data-testid={`${tid}-row`}>
              <Checkbox checked={!!val} onCheckedChange={(v) => setCustom(cf.id, !!v)} className={err(cf) ? 'border-red-500' : ''} data-testid={tid} />
              <span className={err(cf) ? 'text-red-600 font-medium' : 'text-slate-700'}>{cf.label}{cf.required ? ' *' : ''}</span>
            </label>
          );
        }
        if (cf.type === 'select') {
          return (
            <div key={cf.id}>
              <Label className={err(cf) ? 'text-red-600' : ''}>{cf.label}{cf.required ? ' *' : ''}</Label>
              <Select value={val || ''} onValueChange={(v) => setCustom(cf.id, v)}>
                <SelectTrigger className={ec(cf)} data-testid={tid}><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                <SelectContent>
                  {(cf.options || []).map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        if (cf.type === 'textarea') {
          return (
            <div key={cf.id}>
              <Label className={err(cf) ? 'text-red-600' : ''}>{cf.label}{cf.required ? ' *' : ''}</Label>
              <Textarea rows={3} className={ec(cf)} value={val || ''} onChange={(e) => setCustom(cf.id, e.target.value)} data-testid={tid} />
            </div>
          );
        }
        return (
          <div key={cf.id}>
            <Label className={err(cf) ? 'text-red-600' : ''}>{cf.label}{cf.required ? ' *' : ''}</Label>
            <Input className={ec(cf)} value={val || ''} onChange={(e) => setCustom(cf.id, e.target.value)} data-testid={tid} />
          </div>
        );
      })}
    </div>
  );
}

export function AvailabilityDots({ availability }) {
  const meta = AVAILABILITY_META[availability] || AVAILABILITY_META.free;
  return (
    <div className="flex flex-col gap-1 items-center shrink-0" title={meta.label} data-testid="course-offer-availability">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`h-2.5 w-2.5 rounded-full ${i === meta.active ? meta.color : 'bg-slate-200'}`} />
      ))}
    </div>
  );
}

export function OfferDetail({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="font-semibold text-slate-900">{label}</div>
      <div className="text-slate-600 whitespace-pre-line">{value}</div>
    </div>
  );
}

export function LegalBlock({ title, text, url, testId }) {
  if (!text && !url) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60" data-testid={testId}>
      <div className="font-medium text-slate-900 mb-1">{title}</div>
      {text && (
        <div className="max-h-40 overflow-y-auto text-sm text-slate-600 whitespace-pre-line">
          {text}
        </div>
      )}
      {url && (
        <a href={url} target="_blank" rel="noreferrer" className="text-sm text-brand-red hover:underline inline-block mt-2">
          {title} als PDF / Webseite öffnen
        </a>
      )}
    </div>
  );
}

export function SummaryCard({ title, children }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{title}</div>
      <div className="text-sm text-slate-900 leading-relaxed">{children}</div>
    </div>
  );
}
